import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { CheckBox, Input } from '@rneui/themed';
import { useForm, Controller } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import { Alert } from '../common/Alert';
import ComponentContainer from '../common/ComponentContainer';
import Button from '../common/Button';
import Loading from '../common/Loading';
import Recaptcha from '../common/ReCaptcha';
import { StyledWebView } from '../common/StyledWebView';
import { H3, H4, H6, Paragraph2 } from '../text';
import Utils from '../../common/Utils';
import AppContext from '../../contexts/App';
import LayoutContext from '../../contexts/Layout';
import useAxios from '../../hooks/useAxios';

const STYLED_WEB_VIEW_ID = 'emailSubscriptionForm';
const INTEREST_TYPE = 'interest';
const NEWSLETTER_TYPE = 'newsletter';

const EmailSubscriptionForm = ({
    campaign,
    cmsKey,
    introText,
    emailSubscriptionsTitle,
    topicInterestSubscriptionContent,
    topicInterestSubscriptionTitle,
    marketingAdvisory,
    termsOfServiceText,
    submitButtonLabel,
    buttonBackgroundColor,
    physicalWidth,
    width,
    nodeTypeAlias = 'EmailSubscriptionForm'
}) => {
    const { postToApiServer } = useAxios();
    const {
        state: { subscriptionSettings }
    } = useContext(AppContext);
    const { layout } = useContext(LayoutContext);
    const recaptchaRef = useRef();
    const [displayResponse, setDisplayResponse] = useState(false);
    const [checkboxState, setCheckboxState] = useState({
        newsletterCheckAll: false,
        interestCheckAll: false,
        termsAccepted: false,
        updateCheckboxes: false
    });

    const mnemonic = campaign?.mnemonic;

    const {
        clearErrors,
        control,
        getValues,
        handleSubmit,
        reset,
        formState: { errors, isValid, isSubmitting }
    } = useForm({
        mode: 'onBlur',
        shouldFocusError: true,
        shouldUnregister: true,
        defaultValues: {
            firstname: '',
            lastname: '',
            email: '',
            confirmEmail: ''
        }
    });

    const { newsletterCheckAll, interestCheckAll, termsAccepted, updateCheckboxes } = checkboxState;

    const interests = useMemo(() => {
        return subscriptionSettings?.interests?.map(interest => {
            return {
                ...interest,
                checked: false,
                type: INTEREST_TYPE,
                checkId: uuidv4(),
                mnemonic: `${interest.mnemonic}_N`
            };
        });
    }, [subscriptionSettings?.interests]);

    const newsletters = useMemo(() => {
        try {
            return subscriptionSettings.newsletters.map(newsletter => {
                return {
                    ...newsletter,
                    checked: false,
                    type: NEWSLETTER_TYPE,
                    checkId: uuidv4(),
                    mnemonic: `${newsletter.mnemonic}_N`
                };
            });
        } catch (err) {}

        return null;
    }, [subscriptionSettings?.newsletters]);

    const validateEmailDomain = useCallback(async () => {
        const email = getValues('email');

        if (Utils.ValidEmailRegExp.test(email.toLowerCase())) {
            const result = await postToApiServer('/Util/Address/ValidateEmailDomain', { email }, 'application/x-www-form-urlencoded');

            if (result) {
                if (result.valid) {
                    clearErrors(['email', 'confirmEmail']);
                    return true;
                } else {
                    return result.message;
                }
            } else {
                return 'Unable to verify email';
            }
        }
    }, [clearErrors, getValues, postToApiServer]);

    const isFormValid = () => {
        if (newsletters?.filter(item => item.checked === true).length !== 0) {
            if (termsAccepted) {
                if (isValid) {
                    return true;
                }
            }
        }

        return false;
    };

    const subscribe = async data => {
        if (!errors || Object.keys(errors).length === 0) {
            await recaptchaRef.current?.executeAsync();

            const buildRequest = () => {
                const subscriptionChoices = newsletters?.reduce((accumlator, item) => {
                    if (item.checked) {
                        accumlator.push(item.title);
                    }

                    return accumlator;
                }, []);

                // const interestsRequest = interests?.map(item => item.checked);

                return {
                    key: cmsKey,
                    campaign: mnemonic,
                    firstName: data.firstname,
                    lastName: data.lastname,
                    emailAddress: data.email,
                    subscriptionChoices: subscriptionChoices,
                    // interestChoices: interestsRequest,
                    reCaptchaResponse: recaptchaRef.current?.getValue()
                };
            };

            const response = await postToApiServer('/NewsletterSubscription/Subscribe', buildRequest(), 'application/x-www-form-urlencoded; charset=UTF-8');

            recaptchaRef.current?.reset();

            if (!response) {
                Alert('Unexpected Error!\n Please try again.');
            } else {
                Alert(response.success ? response.successMsg.replaceAll('<p>', '').replaceAll('</p>', '') : response.errorMsg.replaceAll('<p>', '').replaceAll('</p>', ''));

                //RESET ALL FORM STATES
                reset({ firstname: '', lastname: '', email: '', confirmEmail: '' });

                interests.forEach(item => {
                    let cleanMnemonic = item.mnemonic.slice(0, item.mnemonic.length - 2);
                    (item.checked = false), (item.mnemonic = `${cleanMnemonic}_N`);
                });

                newsletters.forEach(item => {
                    let cleanMnemonic = item.mnemonic.slice(0, item.mnemonic.length - 2);
                    (item.checked = false), (item.mnemonic = `${cleanMnemonic}_N`);
                });

                setCheckboxState(prev => ({ ...prev, termsAccepted: false, interestCheckAll: false, newsletterCheckAll: false }));
            }
        }
    };

    const handleCheckboxChange = (id, data) => {
        const hasId = data?.find(item => item.checkId === id);

        if (hasId !== undefined) {
            const updateCheckboxArray = data?.forEach(item => {
                if (id === item.checkId) {
                    let cleanMnemonic = item.mnemonic.slice(0, item.mnemonic.length - 2);
                    (item.mnemonic = item.mnemonic.includes('_N') ? `${cleanMnemonic}_Y` : item.mnemonic.includes('_Y') ? `${cleanMnemonic}_N` : item.mnemonic),
                        (item.checked = !item.checked);
                }
            });

            data = updateCheckboxArray;
            setCheckboxState(prev => ({ ...prev, updateCheckboxes: !updateCheckboxes }));
        }
    };

    const handleAllCheck = type => {
        if (type === NEWSLETTER_TYPE) {
            setCheckboxState(prev => ({ ...prev, newsletterCheckAll: !newsletterCheckAll }));

            newsletters?.forEach(item => {
                let cleanMnemonic = item.mnemonic.slice(0, item.mnemonic.length - 2);

                if (!newsletterCheckAll) {
                    item.checked = true;
                    item.mnemonic = `${cleanMnemonic}_Y`;
                } else {
                    item.checked = false;
                    item.mnemonic = `${cleanMnemonic}_N`;
                }
            });
        }

        if (type === INTEREST_TYPE) {
            setCheckboxState(prev => ({ ...prev, interestCheckAll: !interestCheckAll }));

            interests?.forEach(item => {
                let cleanMnemonic = item.mnemonic.slice(0, item.mnemonic.length - 2);

                if (!interestCheckAll) {
                    item.checked = true;
                    item.mnemonic = `${cleanMnemonic}_Y`;
                } else {
                    item.checked = false;
                    item.mnemonic = `${cleanMnemonic}_N`;
                }
            });
        }

        setCheckboxState(prev => ({ ...prev, updateCheckboxes: !updateCheckboxes }));
    };

    const CustomMultipleCheckbox = ({ data, handleChange }) => {
        return (
            <>
                {data?.map(item => (
                    <CheckBox
                        checked={item.checked}
                        containerStyle={{ margin: 0, marginBottom: layout.styleGuide.spacer3, marginLeft: layout.styleGuide.indent, padding: 0 }}
                        dataSet={{ spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`, spacerLeft: `StyleGuide.indent(${layout.styleGuide.indent})` }}
                        key={item.checkId}
                        onPress={() => handleChange(item.checkId, data)}
                        size={18}
                        title={
                            <H6
                                style={{ marginLeft: layout.styleGuide.indent }}
                                text={item.title}
                                dataSet={{ spacerLeft: `StyleGuide.indent(${layout.styleGuide.indent})` }}
                            />
                        }
                        wrapperStyle={{ margin: 0, padding: 0 }}
                    />
                ))}
            </>
        );
    };

    const renderFormInputs = () => {
        return (
            <View>
                <View>
                    <View style={{ flexDirection: layout.device.isMobile ? 'column' : 'row', width: layout.device.isMobile ? '100%' : '50%' }}>
                        <Controller
                            control={control}
                            name='firstname'
                            render={({ field: { onChange, onBlur, value } }) => (
                                <Input
                                    containerStyle={{ width: '100%' }}
                                    errorMessage={errors?.firstname?.message}
                                    errorStyle={Styles.error}
                                    inputStyle={Styles.input}
                                    onBlur={onBlur}
                                    onChangeText={value => onChange(value)}
                                    placeholder='First Name *'
                                    placeholderTextColor='#7f868b'
                                    type='text'
                                    value={value}
                                />
                            )}
                            rules={{
                                pattern: {
                                    message: 'First name cannot contain numbers',
                                    value: Utils.ValidNameRegExp
                                },
                                required: {
                                    message: 'Please enter your first name.',
                                    value: true
                                }
                            }}
                        />
                        <Controller
                            control={control}
                            name='lastname'
                            render={({ field: { onChange, onBlur, value }, fieldState }) => (
                                <Input
                                    containerStyle={{ width: '100%' }}
                                    errorMessage={errors?.lastname?.message}
                                    errorStyle={Styles.error}
                                    inputStyle={Styles.input}
                                    onBlur={onBlur}
                                    onChangeText={value => onChange(value)}
                                    placeholder='Last Name *'
                                    placeholderTextColor='#7f868b'
                                    type='text'
                                    value={value}
                                />
                            )}
                            rules={{
                                pattern: {
                                    message: 'Last name cannot contain numbers',
                                    value: Utils.ValidNameRegExp
                                },
                                required: {
                                    message: 'Please enter your last name.',
                                    value: true
                                }
                            }}
                        />
                    </View>
                    <View style={{ flexDirection: layout.device.isMobile ? 'column' : 'row', width: layout.device.isMobile ? '100%' : '50%' }}>
                        <Controller
                            control={control}
                            name='email'
                            render={({ field: { onChange, onBlur, value } }) => (
                                <Input
                                    containerStyle={{ width: '100%' }}
                                    errorMessage={errors?.email?.message}
                                    errorStyle={Styles.error}
                                    inputStyle={Styles.input}
                                    onBlur={onBlur}
                                    onChangeText={value => onChange(value)}
                                    placeholder='Email *'
                                    placeholderTextColor='#7f868b'
                                    type='text'
                                    value={value}
                                />
                            )}
                            rules={{
                                required: {
                                    message: 'Please enter your email address.',
                                    value: true
                                },
                                pattern: {
                                    message: 'Not a valid email address',
                                    value: Utils.ValidEmailRegExp
                                },
                                validate: async () => await validateEmailDomain()
                            }}
                        />
                        <Controller
                            control={control}
                            name='confirmEmail'
                            render={({ field: { onChange, onBlur, value } }) => (
                                <Input
                                    containerStyle={{ width: '100%' }}
                                    errorMessage={errors?.confirmEmail?.message}
                                    errorStyle={Styles.error}
                                    inputStyle={Styles.input}
                                    onBlur={onBlur}
                                    onChangeText={value => onChange(value)}
                                    placeholder='Confirm Email *'
                                    placeholderTextColor='#7f868b'
                                    type='text'
                                    value={value}
                                />
                            )}
                            rules={{
                                required: {
                                    message: 'Please verify your email address.',
                                    value: true
                                },
                                validate: () => {
                                    const email = getValues('email');
                                    const confirmEmail = getValues('confirmEmail');

                                    return email?.equals(confirmEmail.toLowerCase()) || 'Emails do not match';
                                }
                            }}
                        />
                    </View>
                </View>
                <View style={{ flexDirection: 'row', marginLeft: 5, marginVertical: 5, width: '100%' }}>
                    <Paragraph2 text='* denotes required' />
                </View>
            </View>
        );
    };

    const renderTopSection = () => {
        return (
            <View>
                <H4 text={introText} style={{ marginBottom: layout.styleGuide.spacer3 }} dataSet={{ spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})` }} />
                <View>{renderFormInputs()}</View>
                <H3
                    dataSet={{ spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`, spacerTop: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}
                    style={{ marginBottom: layout.styleGuide.spacer3, marginTop: layout.styleGuide.spacer2 }}
                    text={emailSubscriptionsTitle}
                />
                <View style={{ flexDirection: 'row' }}>
                    <View>
                        <CheckBox
                            checked={newsletterCheckAll}
                            containerStyle={{ margin: 0, marginBottom: layout.styleGuide.spacer3, marginLeft: layout.styleGuide.indent, padding: 0 }}
                            dataSet={{ spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`, spacerLeft: `StyleGuide.indent(${layout.styleGuide.indent})` }}
                            onPress={() => handleAllCheck(NEWSLETTER_TYPE)}
                            size={18}
                            title={
                                <H6
                                    style={{ marginLeft: layout.styleGuide.indent }}
                                    text='Check All'
                                    dataSet={{
                                        spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`,
                                        spacerLeft: `StyleGuide.indent(${layout.styleGuide.indent})`
                                    }}
                                />
                            }
                            wrapperStyle={{ margin: 0, padding: 0 }}
                        />
                        <CustomMultipleCheckbox data={newsletters} handleChange={handleCheckboxChange} />
                    </View>
                </View>
            </View>
        );
    };

    const renderBottomSection = () => {
        return (
            <View>
                <View>
                    <H3
                        dataSet={{ spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`, spacerTop: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}
                        style={{ marginBottom: layout.styleGuide.spacer3, marginTop: layout.styleGuide.spacer2 }}
                        text={topicInterestSubscriptionTitle}
                    />
                    <StyledWebView html={topicInterestSubscriptionContent} nodeId={STYLED_WEB_VIEW_ID} width={width} />
                </View>
                <View style={{ flexDirection: 'row' }}>
                    <View>
                        <CheckBox
                            checked={interestCheckAll}
                            containerStyle={{ margin: 0, marginBottom: layout.styleGuide.spacer3, marginLeft: layout.styleGuide.indent, padding: 0 }}
                            dataSet={{ spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`, spacerLeft: `StyleGuide.indent(${layout.styleGuide.indent})` }}
                            onPress={() => handleAllCheck(INTEREST_TYPE)}
                            size={18}
                            title={
                                <H6
                                    style={{ marginLeft: layout.styleGuide.indent }}
                                    text='Check All'
                                    dataSet={{
                                        spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`,
                                        spacerLeft: `StyleGuide.indent(${layout.styleGuide.indent})`
                                    }}
                                />
                            }
                            wrapperStyle={{ margin: 0, padding: 0 }}
                        />
                        <CustomMultipleCheckbox data={interests} handleChange={handleCheckboxChange} />
                    </View>
                </View>
                <View>
                    <StyledWebView html={marketingAdvisory} nodeId={STYLED_WEB_VIEW_ID} width={physicalWidth || width} />
                </View>
                <View>
                    <CheckBox
                        checked={termsAccepted}
                        containerStyle={{ marginVertical: layout.styleGuide.spacer3, padding: 0 }}
                        fontFamily='GothamBook'
                        onIconPress={() => setCheckboxState(prev => ({ ...prev, termsAccepted: !termsAccepted }))}
                        size={18}
                        title={
                            <H6
                                numberOfLines={3}
                                style={{ marginLeft: layout.styleGuide.indent }}
                                text={termsOfServiceText}
                                dataSet={{ spacerLeft: `StyleGuide.indent(${layout.styleGuide.indent})` }}
                            />
                        }
                    />
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
                    <Button
                        disabled={!isFormValid()}
                        onPress={handleSubmit(subscribe)}
                        style={{
                            borderColor: '#000',
                            borderWidth: StyleSheet.hairlineWidth,
                            backgroundColor: !isFormValid() ? '#f7f8f9' : buttonBackgroundColor ? `#${buttonBackgroundColor}` : '#a61a2b'
                        }}
                        text={submitButtonLabel}
                        textStyle={{ color: !isFormValid() ? '#000' : '#fff' }}
                    />
                </View>
            </View>
        );
    };

    if (!newsletters) {
        return null;
    }

    return (
        <ComponentContainer style={{ backgroundColor: '#fff' }} dataSet={{ umbracoComponentName: nodeTypeAlias }}>
            {renderTopSection()}
            {renderBottomSection()}
            <Recaptcha ref={recaptchaRef} />
            {isSubmitting && <Loading />}
        </ComponentContainer>
    );
};

const Styles = StyleSheet.create({
    error: {
        color: 'red',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '14pt' : 14,
        lineHeight: Platform.OS.equals('web') ? '16pt' : 16
    },
    input: {
        fontFamily: 'GothamBook',
        fontSize: Platform.OS === 'web' ? '16pt' : 16,
        paddingBottom: Platform.OS === 'web' ? '2pt' : 2,
        paddingLeft: Platform.OS === 'web' ? '3pt' : 3,
        paddingTop: Platform.OS === 'web' ? '4pt' : 4
    },
    submitButtonTitle: {
        color: '#fff',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '18pt' : 18,
        letterSpacing: 0,
        lineHeight: Platform.OS.equals('web') ? '20pt' : 20
    }
});

EmailSubscriptionForm.propTypes = {
    buttonBackgroundColor: PropTypes.string,
    campaign: PropTypes.shape({
        domain: PropTypes.arrayOf(PropTypes.string),
        id: PropTypes.string,
        key: PropTypes.string,
        mnemonic: PropTypes.string
    }).isRequired,
    cmsKey: PropTypes.string.isRequired,
    emailSubscriptionsTitle: PropTypes.string,
    introText: PropTypes.string,
    marketingAdvisory: PropTypes.string,
    submitButtonLabel: PropTypes.string,
    termsOfServiceText: PropTypes.string,
    topicInterestSubscriptionContent: PropTypes.string,
    topicInterestSubscriptionTitle: PropTypes.string
};

export default EmailSubscriptionForm;
